import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { formatNumber, NumberFormatPreset, QuickViewWidget, useScrollIntoView } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { useDateRange } from '../../components/DateRangeProvider';
import { I18nNamespace } from '~/common/types';
import { SpendType } from '~/common/types/cubes/SpendType';
import { useResponsibleUsersFilter } from '~/components/ResponsibleUsersProvider';
import { useSpendTypeFilter } from '~/hooks';
import { convertDateRangeToTimeDimension, getSegments } from '~/util/cube';

const formatValueCompact = formatNumber(NumberFormatPreset.CURRENCY_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.CURRENCY);

type NegotiationSpendWidgetProps = {
  filters?: Filter[];
  spendType: SpendType;
};
export const CUBE = 'AverageNegotiationSavings';

const WIDGET_NAME = 'NegotiationSpendWidget';

export const NegotiationSpendWidget = ({ filters, spendType }: NegotiationSpendWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: WIDGET_NAME });
  const [widgetRef, active] = useScrollIntoView({ hash: 'negotiation-spend-widget' });
  const { currentDateRange } = useDateRange();
  const responsibleUsersFilter = useResponsibleUsersFilter(`${CUBE}.responsible_uuid`);
  const spendTypeFilter = useSpendTypeFilter({ spendType, cubeTotalVolumeField: `${CUBE}.quote_total` });
  const queryValue = useCubeQuery({
    measures: [`${CUBE}.total_volume`],
    timeDimensions: convertDateRangeToTimeDimension(`${CUBE}.date`, currentDateRange),
    filters: [...(filters ?? []), responsibleUsersFilter, ...spendTypeFilter],
  });

  const { totalValue } = getSegments(queryValue);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={queryValue.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatValueCompact(totalValue)}
      valueTooltip={formatValue(totalValue)}
      data-intercom-target="WIDGET_NAME"
    />
  );
};
